<template>
  <div class="bg" :class="{ 'is-embedded': isEmbedded }">
    <div v-if="!isEmbedded" class="left_bg"></div>
    <div v-if="!isEmbedded" class="right_bg"></div>
    <div class="market-page" :class="{ 'market-page--embedded': isEmbedded }">
      <div class="market-page__sticky">
        <div v-if="!isEmbedded" class="market-header">
          <div class="market-header__brand">
            <div class="market-header__icon">
              <i class="icon-apps2-ai-line t-iconfont" />
            </div>
            <h1 class="market-header__title">生态市场</h1>
          </div>

          <div aria-label="生态市场分类" class="market-header__tabs" role="tablist">
            <button
              v-for="tab in tabs"
              :key="tab.alias"
              :aria-selected="activeTab === tab.alias"
              class="market-tab"
              :class="{ 'market-tab--active': activeTab === tab.alias }"
              role="tab"
              :tabindex="activeTab === tab.alias ? 0 : -1"
              type="button"
              @click="handleTabChange(tab.alias)"
            >
              <span class="market-tab__label">{{ tab.name }}</span>
              <span class="market-tab__indicator"></span>
            </button>
          </div>

          <!-- 占位 -->
          <div style="width: 112px"></div>
          <!-- <div class="market-header__search">
            <i class="icon-search-line market-header__search-icon t-iconfont"></i>
            <input
              id="market-search"
              v-model="keyword"
              class="market-header__search-input"
              placeholder="搜索数据源"
              type="text"
            />
          </div> -->
        </div>
        <div
          v-if="currentGroups.length"
          aria-label="生态市场标签"
          class="market-tags"
          role="tablist"
        >
          <button
            v-for="(group, tagIndex) in currentGroups"
            :key="group.key"
            :aria-selected="activeTag === tagIndex"
            class="market-tag"
            :class="{ 'market-tag--active': activeTag === tagIndex }"
            role="tab"
            :tabindex="activeTag === tagIndex ? 0 : -1"
            type="button"
            @click="handleTagChange(tagIndex)"
          >
            {{ group.label }}
          </button>
        </div>
      </div>

      <div
        ref="contentRef"
        v-loading="pageLoading"
        class="market-content"
        @scroll="handleContentScroll"
      >
        <template v-if="currentGroups.length && activeTab !== 'bestCompany'">
          <section
            v-for="(group, groupIndex) in currentGroups"
            :key="group.key"
            :ref="(el) => setTagRef(groupIndex, el)"
            class="market-group"
          >
            <h2 class="market-group__title">{{ group.label }}</h2>
            <div class="market-section__grid">
              <article
                v-for="card in group.cards"
                :key="card.key"
                class="market-card"
                role="button"
                tabindex="0"
                @click="handleOpenDetail(card)"
              >
                <img :alt="card.name" class="market-card__cover" :src="card.cover || card.icon" />
                <div class="market-card__body">
                  <div class="market-card__header">
                    <img
                      :alt="card.name"
                      :class="[isAssistantTab ? 'market-card__avatar' : 'market-card__box']"
                      :src="card.icon"
                    />
                    <h3 class="market-card__name">{{ card.name }}</h3>
                  </div>
                  <p class="market-card__desc">{{ card.profile || card.details }}</p>
                  <div class="market-card__footer">
                    <template v-if="isAssistantTab">
                      <span class="market-card__label">提供方：</span>
                      <span class="market-card__value">{{
                        card.supplier || '杭州逍邦科技有限公司'
                      }}</span>
                    </template>
                    <template v-else>
                      <span class="market-card__price-amount">{{ card.cost }}</span>
                    </template>
                  </div>
                </div>
              </article>
            </div>
          </section>
        </template>
        <template v-else-if="activeTab === 'bestCompany'">
          <div class="best-practice-grid">
            <article
              v-for="card in bestPracticeDemoList"
              :key="card.alias"
              class="best-practice-card"
              role="button"
              tabindex="0"
              @click="handleGoCompany(card.alias)"
            >
              <img :alt="card.name" class="best-practice-card__cover" :src="card.cover" />
              <div class="best-practice-card__body">
                <h3 class="best-practice-card__name">{{ card.name }}</h3>
                <p class="best-practice-card__desc">{{ card.profile }}</p>
              </div>
            </article>
          </div>
        </template>
      </div>
    </div>

    <MarketDetailDialog ref="marketDetailDialogRef" :active-tab="activeTab" :is-admin="isAdmin" />
  </div>
</template>

<script setup>
import { getServiceAccessList } from '@/api'
import { computed, nextTick, ref, watch, provide, onMounted } from 'vue'
import utils from '@/utils'
import MarketDetailDialog from './components/market-detail-dialog.vue'
import { bestPracticeDemoList } from './best-company.js'

const props = defineProps({
  tabAlias: { type: String, default: '' }
})
const isEmbedded = computed(() => Boolean(props.tabAlias))
const userId = utils.xbbLocalStorageGet('userId') || ''
const serviceList = ref([])
const pageLoading = ref(false)
const activeTab = ref('')
const activeTag = ref(0)
const marketDetailDialogRef = ref(null)
const tagRefs = ref({})
const contentRef = ref(null)
const isTagScrolling = ref(false)

const tabs = computed(() => serviceList.value)

const isAssistantTab = computed(() => activeTab.value === 'aiAgent')

const currentGroups = computed(() => {
  const tab =
    tabs.value.find((t) => t.alias === activeTab.value) ||
    (!isEmbedded.value ? tabs.value[0] : null)
  return (tab?.subList || [])
    .map((group) => {
      const cards = (group.subList || []).map((card) => ({ ...card, tagLabel: group.name }))
      return cards.length ? { key: group.name, label: group.name, cards } : null
    })
    .filter(Boolean)
})

const syncActiveSelection = () => {
  const validTab = tabs.value.find((t) => t.alias === activeTab.value)
  activeTab.value = props.tabAlias || (validTab ? activeTab.value : tabs.value[0]?.alias || '')
  activeTag.value = currentGroups.value[activeTag.value] ? activeTag.value : 0
}

const isAdmin = ref(false)
/**
 * 获取接入列表
 */
const getAccessList = async () => {
  pageLoading.value = true

  try {
    const { result } = await getServiceAccessList({ userId })
    isAdmin.value = Boolean(result?.isAdminOrBoss || result?.isManager)
    serviceList.value = result?.list || []
    // 写入静态样板间
    serviceList.value.push({
      alias: 'bestCompany',
      name: '行业最佳实践'
    })

    syncActiveSelection()
  } catch (error) {
    console.error('获取生态市场列表失败:', error)
    serviceList.value = []
    activeTab.value = ''
    activeTag.value = 0
  } finally {
    pageLoading.value = false
  }
}

onMounted(() => {
  getAccessList()
})

provide('getAccessList', getAccessList)

const setTagRef = (tagIndex, element) => {
  if (!element) {
    delete tagRefs.value[tagIndex]
    return
  }

  tagRefs.value[tagIndex] = element
}

const syncActiveTagByScroll = () => {
  if (isTagScrolling.value) {
    return
  }

  const container = contentRef.value
  const groups = currentGroups.value

  if (!container || !groups.length) {
    return
  }

  const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 2
  let currentIndex = 0

  if (isNearBottom) {
    currentIndex = groups.length - 1
  } else {
    const containerTop = container.getBoundingClientRect().top

    groups.forEach((group, index) => {
      const element = tagRefs.value[index]

      if (!element) {
        return
      }

      const { top } = element.getBoundingClientRect()
      if (top - containerTop <= 12) {
        currentIndex = index
      }
    })
  }

  if (activeTag.value !== currentIndex) {
    activeTag.value = currentIndex
  }
}

const scrollToTag = (tagIndex) => {
  isTagScrolling.value = true

  nextTick(() => {
    const target = tagRefs.value[tagIndex]
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      })
    }

    window.setTimeout(() => {
      isTagScrolling.value = false
      syncActiveTagByScroll()
    }, 300)
  })
}

const handleContentScroll = () => {
  syncActiveTagByScroll()
}

const handleTabChange = (alias) => {
  activeTab.value = alias
  activeTag.value = 0
}

watch(
  () => props.tabAlias,
  (alias) => {
    if (!alias) return
    handleTabChange(alias)
    nextTick(() => {
      if (contentRef.value) contentRef.value.scrollTop = 0
    })
  },
  { immediate: true }
)

const handleTagChange = (tagIndex) => {
  activeTag.value = tagIndex
  scrollToTag(tagIndex)
}

watch(currentGroups, (groups) => {
  const nextTag = groups[activeTag.value] ? activeTag.value : 0

  if (activeTag.value !== nextTag) {
    activeTag.value = nextTag
  }

  nextTick(() => {
    syncActiveTagByScroll()
  })
})

const handleOpenDetail = (card) => {
  marketDetailDialogRef.value?.open(card)
}

const handleGoCompany = (alias) => {
  const demoUrl = new URL(
    'https://xbb-cplus.oss-cn-hangzhou.aliyuncs.com/webhook/ai-demo/index.html'
    // 'http://127.0.0.1:5500/index.html'
  )
  demoUrl.searchParams.set('type', alias)
  window.open(demoUrl.toString(), '_blank')
}
</script>

<style lang="scss" scoped>
.bg {
  background: #f5f7fb;
  height: 100%;
  box-sizing: border-box;
  position: relative;
  &.is-embedded {
    min-height: 0;
    background: #ffffff;
  }
  .left_bg {
    position: absolute;
    left: 369px;
    top: -155px;
    width: 955px;
    height: 198px;

    /* 链接/Link-1 浅色白底悬浮 */
    /* 样式描述：$link-color-1 */
    background: #e8f7ff;

    filter: blur(64px);
  }
  .right_bg {
    position: absolute;
    right: -50px;
    top: -150px;
    width: 795.46px;
    height: 204.12px;
    transform: rotate(10.9deg);
    opacity: 0.5;

    /* 拓展色/紫罗兰/Violets-1 */
    /* 样式描述：$violets-color-1 */
    background: #f5e8ff;

    filter: blur(64px);
  }
}
.market-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 1100px;
  padding-top: 52px;
  margin: 0 auto;
  box-sizing: border-box;
  overflow: hidden;

  &--embedded {
    max-width: 1148px;
    padding: 48px 24px 0;
    .market-tags {
      margin-top: 0;
    }
    .market-card {
      width: auto;
      min-width: 0;
    }
  }

  &__sticky {
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
    padding: 0 4px 24px 4px;
  }
}

.market-header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;

  &__brand {
    display: flex;
    align-items: center;
    margin-right: 8px;
  }

  &__icon {
    position: relative;
    width: 24px;
    height: 24px;
    margin-right: 8px;

    .icon-apps2-ai-line {
      font-size: 24px;
      background: linear-gradient(315deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
      color: transparent;
      background-clip: text;
    }
  }

  &__title {
    margin: 0;
    font-family: 'PingFang SC', sans-serif;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
    color: #323233;
  }

  &__tabs {
    z-index: 999;
    display: flex;
    justify-content: center;
    flex: 1;
    min-width: 0;
  }

  &__search {
    z-index: 999;
    display: flex;
    align-items: center;
    width: 280px;
    height: 36px;
    padding: 8px 12px;
    box-sizing: border-box;
    border: 1px solid #e5e6eb;
    border-radius: 24px;
    background: #f2f3f5;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease,
      background 0.2s ease;

    &:focus-within {
      background: #ffffff;
      border-color: #7a52f4;
      box-shadow: 0 0 0 3px rgba(114, 46, 209, 0.08);
    }
  }

  &__search-icon {
    margin-right: 8px;
    font-size: 16px;
    color: #969799;
  }

  &__search-input {
    width: 100%;
    border: 0;
    outline: none;
    background: transparent;
    padding: 0;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #323233;

    &::placeholder {
      color: #969799;
    }
  }
}

.market-tab {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 66px;
  height: 27px;
  padding: 0 12px;
  border: 0;
  border-radius: 64px;
  background: transparent;
  cursor: pointer;
  transition: color 0.2s ease;

  &__label {
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #646566;
    transition:
      color 0.2s ease,
      font-weight 0.2s ease;
  }

  &__indicator {
    width: 20px;
    height: 3px;
    border-radius: 4px;
    background: transparent;
    transition: background 0.2s ease;
  }

  &:hover .market-tab__label {
    color: #323233;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(114, 46, 209, 0.12);
  }

  &--active {
    .market-tab__label {
      color: #323233;
      font-weight: 600;
    }

    .market-tab__indicator {
      background: #7a52f4;
    }
  }
}

.market-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
}

.market-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 4px 12px;
  box-sizing: border-box;
  border: 1px solid #f2f3f5;
  border-radius: 8px;
  background: #ffffff;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #646566;
  cursor: pointer;
  transition:
    color 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    color: #7a52f4;
    border-color: #ead7ff;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(114, 46, 209, 0.12);
  }

  &--active {
    border-color: transparent;
    background: #f5e8ff;
    color: #7a52f4;
  }
}
.market-content {
  flex: 1;
  padding-top: 8px;
  padding-bottom: 20px;

  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-behavior: smooth;
  scrollbar-width: none;

  &__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 240px;
    gap: 8px;
    padding: 48px 24px;
    text-align: center;
  }

  &__eyebrow {
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #7a52f4;
  }

  &__title {
    font-family: 'PingFang SC', sans-serif;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    color: #323233;
  }

  &__desc {
    max-width: 320px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #969799;
  }
}

.market-group {
  scroll-margin-top: 8px;
  padding: 0 3px;
  & + & {
    margin-top: 30px;
  }

  &__title {
    margin: 0 0 12px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #323233;
  }
}

.market-section {
  & + & {
    margin-top: 24px;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
  }
}

.market-card {
  display: flex;
  width: 360px;
  height: 180px;
  overflow: hidden;
  border: 2px solid #ffffff;
  box-shadow: 0px 0px 4px 0px rgba(37, 13, 70, 0.1);
  border-radius: 16px;
  background: #f7f8fa;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(50, 50, 51, 0.08);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(114, 46, 209, 0.12);
  }

  &__cover {
    outline: 2px solid #ffffff;
    border-radius: 16px;
    width: 120px;
    height: 180px;
    object-fit: contain;
  }

  &__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    padding: 16px;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 32px;
  }

  &__avatar,
  &__box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #ffffff;
  }

  &__avatar {
    border-radius: 999px;
    object-fit: cover;
    border: 1px solid #ffffff;
    box-shadow: 0px 0px 5.33px 0px rgba(56, 47, 220, 0.2);
  }

  &__name {
    font-family: 'PingFang SC', sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    color: #323233;
  }

  &__desc {
    margin: 8px 0 2px 0;
    height: 82px;
    overflow: hidden;
    font-family: 'PingFang SC', sans-serif;
    font-size: 12px;
    line-height: 16px;
    color: #646566;
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  }

  &__footer {
    display: flex;
    align-items: center;
    margin-top: auto;
    height: 16px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 12px;
    line-height: 16px;
  }

  &__label,
  &__price-unit {
    color: #969799;
  }

  &__value {
    color: #646566;
  }

  &__price-symbol,
  &__price-amount {
    color: #7a52f4;
  }

  &__price-symbol,
  &__price-unit {
    font-size: 14px;
  }

  &__price-amount {
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
  }
}

.best-practice-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 16px;
  padding: 0 4px 20px;
}

.best-practice-card {
  width: 360px;
  height: 302px;
  overflow: hidden;
  border: 2px solid #ffffff;
  border-radius: 24px;
  background: #f7f8fa;
  box-shadow: 0px 0px 4px 0px rgba(37, 13, 70, 0.1);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(50, 50, 51, 0.08);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(114, 46, 209, 0.12);
  }

  &__cover {
    display: block;
    width: 100%;
    height: 200px;
    object-fit: cover;
  }

  &__body {
    padding: 16px;
  }

  &__name {
    margin: 0;
    font-family: 'PingFang SC', sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    color: #323233;
  }

  &__desc {
    display: -webkit-box;
    margin: 8px 0 0;
    overflow: hidden;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #646566;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
}
</style>
